import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarHeart, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, IDEA_MAIL_TEXT, IDEA_MAIL_MAIN, IDEA_MAIL_SUB, ideaMailText } from "@/context/AuthContext";
import api from "@/lib/api";

export const WeeklyIdeasOptIn = ({ checked, onChange, className = "", subtle = false, companyName }) => (
  <label
    className={subtle
      ? `flex gap-2.5 items-start cursor-pointer ${className}`
      : `flex gap-3 items-start cursor-pointer rounded-xl border p-4 transition-colors ${checked ? "border-[#22D3C5] bg-[#22D3C5]/[0.07]" : "border-border bg-card hover:border-[#22D3C5]/60"} ${className}`}
    data-testid="idea-mail-optin"
  >
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} className={subtle ? "mt-0.5 h-3.5 w-3.5" : "mt-0.5"} data-testid="idea-mail-optin-checkbox" />
    <span className={subtle ? "text-xs leading-relaxed text-muted-foreground" : "text-sm leading-relaxed"}>
      {subtle ? ideaMailText(companyName) : (<>
        <span className="text-foreground">{IDEA_MAIL_MAIN}</span>
        <span className="block text-xs text-muted-foreground mt-1">{IDEA_MAIL_SUB}</span>
      </>)}
    </span>
  </label>
);

export const useIdeaMailOptIn = () => {
  const { user, refresh } = useAuth();
  const [checked, setChecked] = useState(false);
  const show = !!user && !user.idea_mail_opt_in;
  const commit = async () => {
    if (!show || !checked) return;
    await api.post("/me/idea-mail", { opt_in: true, consent_text: IDEA_MAIL_TEXT }).catch(() => {});
    refresh?.();
  };
  return { show, checked, setChecked, commit };
};

const DASHBOARD_MAIN = "Få 3 friske idéer til SoMe-opslag i indbakken hver mandag.";
const DASHBOARD_SUB = "Afmeld når som helst med et klik.";

export const IdeaMailDashboardCard = () => {
  const { user, refresh } = useAuth();
  const key = `aicom_idea_mail_dismissed_${user?.id}`;
  const [hidden, setHidden] = useState(() => !!localStorage.getItem(key));
  const [state, setState] = useState("idle");
  if (!user || user.role === "admin" || hidden || user.idea_mail_unsubscribed_at || (user.idea_mail_opt_in && state !== "done")) return null;

  const accept = async () => {
    setState("saving");
    try {
      await api.post("/me/idea-mail", { opt_in: true, consent_text: `${DASHBOARD_MAIN} ${DASHBOARD_SUB}`, source: "dashboard" });
      setState("done");
      refresh?.();
    } catch {
      setState("idle");
      toast.error("Det lykkedes ikke at tilmelde dig. Prøv igen.");
    }
  };
  const dismiss = () => { localStorage.setItem(key, "1"); setHidden(true); };

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="mb-8 rounded-2xl border bg-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
      style={{ borderColor: "#DDD8D0" }} data-testid="idea-mail-card">
      <div className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#22D3C51f" }}>
        {state === "done" ? <Check className="h-5 w-5" style={{ color: "#0E8F86" }} /> : <CalendarHeart className="h-5 w-5" style={{ color: "#0E8F86" }} />}
      </div>
      {state === "done" ? (
        <div className="flex-1" data-testid="idea-mail-card-done">
          <p className="font-medium">Du er tilmeldt ugens SoMe-idéer</p>
          <p className="text-sm text-muted-foreground mt-0.5">Første mail lander i indbakken mandag kl. 7.</p>
        </div>
      ) : (
        <>
          <div className="flex-1">
            <p className="font-medium leading-snug" data-testid="idea-mail-card-title">{DASHBOARD_MAIN}</p>
            <p className="text-sm text-muted-foreground mt-1">{DASHBOARD_SUB}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={dismiss} disabled={state === "saving"} data-testid="idea-mail-card-dismiss">Nej tak</Button>
            <Button onClick={accept} disabled={state === "saving"} className="gap-2 rounded-full" data-testid="idea-mail-card-accept">
              {state === "saving" && <Loader2 className="h-4 w-4 animate-spin" />} Ja tak, send mig idéerne
            </Button>
          </div>
        </>
      )}
    </motion.div>
  );
};
